import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import type { InventoryItem } from '@court-sync/shared';
import {
  Button,
  Drawer,
  Field,
  FieldLabel,
  Input,
  Textarea,
} from '@/components/ui';
import { clubInventoryService } from '@/services/clubInventory';

const HIRE_FORM_ID = 'inventory-hire-form';

export interface InventoryHireFormValues {
  itemId: string;
  hirerName: string;
  quantity: string;
  startAt: string;
  endAt: string;
  notes: string;
}

interface InventoryHireDrawerProps {
  clubId: string;
  items: InventoryItem[];
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

function defaultStart(): string {
  const now = new Date();
  now.setMinutes(0, 0, 0);
  return now.toISOString().slice(0, 16);
}

function defaultEnd(): string {
  const end = new Date();
  end.setMinutes(0, 0, 0);
  end.setHours(end.getHours() + 2);
  return end.toISOString().slice(0, 16);
}

export function InventoryHireDrawer({
  clubId,
  items,
  isOpen,
  onClose,
  onSaved,
}: InventoryHireDrawerProps) {
  const hireableItems = items.filter((item) => item.isActive && item.isHireable);

  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<InventoryHireFormValues>({
    defaultValues: {
      itemId: hireableItems[0]?.id ?? '',
      hirerName: '',
      quantity: '1',
      startAt: defaultStart(),
      endAt: defaultEnd(),
      notes: '',
    },
  });

  useEffect(() => {
    if (isOpen) {
      reset({
        itemId: hireableItems[0]?.id ?? '',
        hirerName: '',
        quantity: '1',
        startAt: defaultStart(),
        endAt: defaultEnd(),
        notes: '',
      });
    }
  }, [isOpen, hireableItems, reset]);

  const onSubmit = async (values: InventoryHireFormValues) => {
    const quantity = Number.parseInt(values.quantity, 10);
    if (!values.itemId) {
      toast.error('Select an item');
      return;
    }
    if (!values.hirerName.trim()) {
      toast.error('Hirer name is required');
      return;
    }
    if (!Number.isFinite(quantity) || quantity < 1) {
      toast.error('Enter a valid quantity');
      return;
    }

    const startAt = new Date(values.startAt);
    const endAt = new Date(values.endAt);
    if (Number.isNaN(startAt.getTime()) || Number.isNaN(endAt.getTime()) || endAt <= startAt) {
      toast.error('End time must be after start time');
      return;
    }

    const res = await clubInventoryService.createHire(clubId, {
      itemId: values.itemId,
      hirerName: values.hirerName.trim(),
      quantity,
      startAt,
      endAt,
      notes: values.notes.trim() || null,
    });

    if (res.success) {
      toast.success('Hire logged');
      onSaved();
      onClose();
    } else {
      toast.error(res.error || 'Failed to log hire');
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Log walk-up hire"
      description="Record equipment loaned outside court hire checkout."
      width="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={HIRE_FORM_ID}
            isLoading={isSubmitting}
            disabled={hireableItems.length === 0}
          >
            Log hire
          </Button>
        </div>
      }
    >
      {hireableItems.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No hireable items yet. Create an item and mark it as available for hire.
        </p>
      ) : (
        <form id={HIRE_FORM_ID} onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <Field>
            <FieldLabel htmlFor="hire-item">Item</FieldLabel>
            <select
              id="hire-item"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              {...register('itemId', { required: true })}
            >
              {hireableItems.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name} ({item.quantity} owned)
                </option>
              ))}
            </select>
          </Field>

          <Field>
            <FieldLabel htmlFor="hire-hirer">Hirer name</FieldLabel>
            <Input id="hire-hirer" {...register('hirerName', { required: true })} />
          </Field>

          <Field>
            <FieldLabel htmlFor="hire-quantity">Quantity</FieldLabel>
            <Input id="hire-quantity" type="number" min={1} {...register('quantity')} />
          </Field>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field>
              <FieldLabel htmlFor="hire-start">Out at</FieldLabel>
              <Input id="hire-start" type="datetime-local" {...register('startAt')} />
            </Field>
            <Field>
              <FieldLabel htmlFor="hire-end">Due back</FieldLabel>
              <Input id="hire-end" type="datetime-local" {...register('endAt')} />
            </Field>
          </div>

          <Field>
            <FieldLabel htmlFor="hire-notes">Notes</FieldLabel>
            <Textarea id="hire-notes" rows={2} {...register('notes')} />
          </Field>
        </form>
      )}
    </Drawer>
  );
}
